/**
 * Consent Management Service
 *
 * API service for consent management endpoints.
 * Provides methods for fetching consent forms and students' consent status.
 */

import apiClient from './apiClient';

/**
 * Get list of consent forms for dropdown filter
 * @returns {Promise<{forms: Array, total: number}>}
 */
export const getConsentForms = async () => {
    try {
        const response = await apiClient.get('/consents/forms');
        return {
            success: true,
            data: response.data,
            message: 'Consent forms retrieved successfully'
        };
    } catch (error) {
        console.error('Error fetching consent forms:', error);
        return {
            success: false,
            data: { forms: [], total: 0 },
            message: error.response?.data?.detail || 'Failed to fetch consent forms'
        };
    }
};

/**
 * Get students filtered by consent status
 * @param {Object} params - Query parameters
 * @param {string} params.form_id - Consent form ID (required)
 * @param {boolean} params.consent_done - Filter by consent done (true) or not done (false)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {string} params.search - Optional search term for student name
 * @returns {Promise<{students: Array, meta: Object}>}
 */
export const getStudentsConsentStatus = async (params) => {
    try {
        const response = await apiClient.get('/consents/students', { params });
        return {
            success: true,
            data: response.data,
            message: 'Students consent status retrieved successfully'
        };
    } catch (error) {
        console.error('Error fetching students consent status:', error);
        return {
            success: false,
            data: { students: [], meta: { page: 1, page_size: 20, total_items: 0, total_pages: 0, has_next: false, has_previous: false } },
            message: error.response?.data?.detail || 'Failed to fetch students consent status'
        };
    }
};

/**
 * Export students consent list as XLSX or PDF
 * @param {Object} params - Query parameters
 * @param {string} params.form_id - Consent form ID (required)
 * @param {boolean} params.consent_done - Filter by consent done (true) or not done (false)
 * @param {string} params.format - Export format: 'xlsx' or 'pdf'
 * @param {string} params.search - Optional search term for student name
 * @returns {Promise} Axios response with blob data
 */
export const exportStudentsConsent = async (params) => {
    const response = await apiClient.get('/consents/students/export', {
        params,
        responseType: 'blob'
    });
    return response;
};

const consentService = {
    getConsentForms,
    getStudentsConsentStatus,
    exportStudentsConsent
};

export default consentService;
